/**
 * Time-clock client. Talks to /api/timeclock — see
 * DineNGo-backend/timeclock/timeclock.controller.js for the wire contract.
 *
 * Every mutating call sends an Idempotency-Key header so the device can
 * retry a flaky request without producing two ClockEvents (the backend
 * caches the response for 24h, keyed on `<scope>:<key>`).
 */
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PunchOutboxService} from './punch-outbox.service';

export interface ClockShift {
  _id?: string;
  orgUuid: string;
  userId: string;
  usernameSnapshot?: string;
  firstNameSnapshot?: string;
  lastNameSnapshot?: string;
  startedAt: string;
  endedAt?: string;
  status: 'open' | 'closed' | 'auto_closed' | 'edited' | 'disputed';
  workedSeconds?: number;
  breakSeconds?: number;
  jobRoleIdAtStart?: string;
  jobRoleLabelAtStart?: string;
  wageCentsAtStart?: number;
  payTypeAtStart?: 'hourly' | 'salary' | 'tipped';
  scheduledStartAt?: string;
  lateMinutes?: number;
}

export interface ClockEvent {
  _id?: string;
  orgUuid: string;
  userId: string;
  type: 'CLOCK_IN' | 'CLOCK_OUT' | 'BREAK_START' | 'BREAK_END' | 'AUTO_CLOSE';
  occurredAt: string;
  recordedAt: string;
  source?: string;
  shiftId?: string;
}

export interface ClockInResponse {
  success: boolean;
  alreadyOpen?: boolean;
  shift: ClockShift;
  event?: ClockEvent;
}

export interface ClockOutResponse {
  success: boolean;
  shift: ClockShift;
  event?: ClockEvent;
  workedSeconds: number;
}

export interface OpenShiftResponse {
  shift: ClockShift | null;
}

export interface PreviewPunchResponse {
  success: boolean;
  user: {_id: string; username: string; firstName?: string; lastName?: string};
  openShift: ClockShift | null;
  recentEvents: ClockEvent[];
  dayKey: string;
}

@Injectable({providedIn: 'root'})
export class ClockEventService {
  constructor(private http: HttpClient, private punchOutbox: PunchOutboxService) {}

  /**
   * Clock the named employee in. Backend verifies `pin` first, then `password`
   * — at least one must be supplied. Returns `alreadyOpen:true` (200, NOT 409)
   * when the employee already has an open shift; the caller should treat that
   * as a no-op and surface the existing shift to the user.
   *
   * Offline path: when the request fails with `status === 0` (HttpClient's
   * "no network" signal — DNS, offline, or full TCP failure) we enqueue
   * the punch on the local outbox and surface a synthetic
   * `{queued: true, success: false}` error so the dialog can show the
   * "Punched offline. Will sync when online." toast instead of the
   * normal failure message.
   */
  clockIn(payload: {
    username: string;
    pin?: string;
    password?: string;
    jobRoleId?: string;
    deviceId?: string;
  }): Observable<ClockInResponse> {
    return this.http.post<ClockInResponse>(
      `${environment.apiUrl}/timeclock/clockIn`,
      payload,
      {headers: this.idempotencyHeader()},
    ).pipe(catchError(err => this.maybeQueueOffline(err, 'clockIn', payload)));
  }

  /**
   * Clock out of the open shift. 409 NO_OPEN_SHIFT if the user wasn't on
   * shift. Offline path mirrors clockIn.
   */
  clockOut(payload: {
    username: string;
    pin?: string;
    password?: string;
    deviceId?: string;
  }): Observable<ClockOutResponse> {
    return this.http.post<ClockOutResponse>(
      `${environment.apiUrl}/timeclock/clockOut`,
      payload,
      {headers: this.idempotencyHeader()},
    ).pipe(catchError(err => this.maybeQueueOffline(err, 'clockOut', payload)));
  }

  /**
   * Decide whether `err` looks like a transport-level network failure
   * (the device is offline, the WiFi died mid-request, etc) versus a
   * server-side rejection (401 bad PIN, 409 no open shift, ...). Only the
   * former gets enqueued — auth/business failures should surface to the
   * user immediately.
   *
   * `status === 0` is HttpClient's canonical "no network" signal in
   * Angular (XHR's readyState 4 with status 0). We also accept the
   * `navigator.onLine === false` fallback for completeness.
   */
  private maybeQueueOffline(
    err: any,
    action: 'clockIn' | 'clockOut',
    payload: {username: string; pin?: string; password?: string},
  ): Observable<never> {
    const status = (err && (err.status ?? (err as HttpErrorResponse)?.status)) || 0;
    const isOffline = status === 0 || !this.punchOutbox.isOnline();
    // RxJS 6 `throwError` takes the error value directly — passing a
    // factory (`() => err`) emits the *function* as the error, which is
    // what the spec was catching. RxJS 7+ added the factory overload but
    // the project is pinned at ^6.5.2 (per package.json), so stick to
    // the v6 form on both branches here.
    if (!isOffline) { return throwError(err); }

    const clientGeneratedId = this.newUuid();
    this.punchOutbox.enqueue({
      clientGeneratedId,
      action,
      username: payload.username,
      pin: payload.pin,
      password: payload.password,
      occurredAt: new Date().toISOString(),
    });
    return throwError({success: false, queued: true, clientGeneratedId, action});
  }

  private newUuid(): string {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c && typeof c.randomUUID === 'function') { return c.randomUUID(); }
    return 'q-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /** Open shift for the JWT'd caller (used to drive the green-dot indicator). */
  getMyOpenShift(): Observable<OpenShiftResponse> {
    return this.http.get<OpenShiftResponse>(`${environment.apiUrl}/timeclock/me/open`);
  }

  /**
   * Verify-only check used by the punch-confirmation dialog. Same dual-auth
   * payload as clockIn/clockOut, but writes nothing — returns the
   * employee's open shift (if any) and today's recent ClockEvents so the
   * UI can render a "you are about to clock in/out" preview before the
   * cashier confirms.
   */
  previewPunch(payload: {
    username: string;
    pin?: string;
    password?: string;
  }): Observable<PreviewPunchResponse> {
    return this.http.post<PreviewPunchResponse>(
      `${environment.apiUrl}/timeclock/preview`,
      payload,
    );
  }

  /** Open shift for any employee in the same org — used by the dialog when the
   *  cashier picks a different employee from the dropdown. */
  getOpenShiftForUsername(username: string): Observable<OpenShiftResponse> {
    return this.http.get<OpenShiftResponse>(
      `${environment.apiUrl}/timeclock/openShift`,
      {params: {username}},
    );
  }

  /**
   * UUID v4 generator suitable for an Idempotency-Key. Uses the platform
   * `crypto.randomUUID` when available (all modern browsers + every device
   * the cashier runs on). Falls back to a Math.random shim only on truly
   * ancient targets — collision risk is irrelevant here because the
   * backend cache is per-org-scope, 24h TTL.
   */
  private idempotencyHeader(): HttpHeaders {
    let key: string;
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c && typeof c.randomUUID === 'function') {
      key = c.randomUUID();
    } else {
      key = 'k-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
    return new HttpHeaders({'Idempotency-Key': key});
  }
}
